import React from 'react';
import {Box, styled} from '@mui/material';
import {Body, designSystemToken} from '@lightricks/react-design-system';
import translate from '@/utils/translate';
import Divider from '@/components/divider';
import {ToneOption, TONE_OPTIONS} from '../../../constants/toneOptions';
import CreativeDescriptionInput from './CreativeDescriptionInput';
import ScriptTypeSelector from './ScriptTypeSelector';
import ToneOfVoiceDropdown from './ToneOfVoiceDropdown';

const MAX_CREATIVE_DESCRIPTION_LENGTH = 150;

const SectionContainer = styled(Box)(() => ({
  display: 'flex',
  width: '100%',
  flexDirection: 'column',
  gap: '24px',
}));

const SubSectionContainer = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
}));

interface CreativeStyleSectionProps {
  testID?: string;

  // Tone of Voice props
  toneValue: string;
  toneOptions?: ToneOption[];
  onToneChange: (value: string) => void;

  // Script Type props
  selectedScriptTypes: string[];
  onScriptTypeSelect: (value: string) => void;

  // Creative Description props
  creativeDescription: string;
  onCreativeDescriptionChange: (value: string) => void;
}

function CreativeStyleSection({
  testID = 'creative-style-section',
  toneValue,
  toneOptions = TONE_OPTIONS,
  onToneChange,
  selectedScriptTypes,
  onScriptTypeSelect,
  creativeDescription,
  onCreativeDescriptionChange,
}: CreativeStyleSectionProps) {
  return (
    <SectionContainer data-testid={testID}>
      <SubSectionContainer>
        <Body size="sm" color={designSystemToken('semantic.fg.primary')}>
          {translate('Tone of Voice')}
        </Body>
        <ToneOfVoiceDropdown
          testID={`${testID}-tone-dropdown`}
          value={toneValue}
          options={toneOptions}
          onChange={onToneChange}
        />
      </SubSectionContainer>

      <Divider margin={0} />

      <ScriptTypeSelector
        testID={`${testID}-script-type`}
        selectedValues={selectedScriptTypes}
        onValueSelect={onScriptTypeSelect}
      />

      <Divider margin={0} />

      <CreativeDescriptionInput
        testID={`${testID}-creative-description`}
        value={creativeDescription}
        onChange={onCreativeDescriptionChange}
        maxWords={MAX_CREATIVE_DESCRIPTION_LENGTH}
        showWordCounter
      />
    </SectionContainer>
  );
}

export default CreativeStyleSection;
